import { useEffect, useRef } from 'react';
import { ApiError } from 'shared/src/api/httpClient';
import { bridge, isWeb, useNavigator } from 'shared/src/utils';
import { getAuthCallbackUri, isSocialProvider } from '../../../socialAuth';
import {
  type PendingSocialAuth,
  useSetPendingSocialAuth,
} from '../../../socialAuthHandoff';
import { usePostKakaoAccessToken } from '../queries/usePostKakaoAccessToken';

/**
 * 소셜 OAuth 콜백(/auth/:provider) 처리 — 1단계: provider 토큰 확보 (웹 전용).
 *
 * provider별 콜백 파라미터에서 access_token(apple은 id_token)만 확보해
 * 전역 아톰에 담고 Login 화면으로 넘긴다. 실제 JWT 발급·후처리는 Login 화면
 * (useConsumePendingSocialAuth)이 담당한다. (2단 구조)
 * - kakao: ?code= → 카카오 access_token 교환
 * - naver/facebook: #access_token=
 * - apple: #id_token=
 * 토큰 없이 진입(사용자 취소 등)하거나 교환 실패 시 로그인 화면으로 이동한다.
 */
export const useSocialAuthCallback = () => {
  const { mutateAsync: postKakaoAccessToken } = usePostKakaoAccessToken();
  const setPendingSocialAuth = useSetPendingSocialAuth();
  const { navigator } = useNavigator();
  // 인가 코드는 1회용이라 effect 중복 실행(StrictMode 등)에서 재처리를 막는다.
  const handledRef = useRef(false);

  useEffect(() => {
    if (!isWeb || handledRef.current) return;
    handledRef.current = true;

    const provider = window.location.pathname.split('/')[2] ?? '';
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    // 복귀 경로(state)는 비밀이 아니므로 URL로 유지한다. moveToLogin이 redirect를
    // 다시 URL(?redirect=)로 실어 Login이 URL에서 읽는다.
    const state = query.get('state') ?? hash.get('state') ?? undefined;

    const obtainToken = async (): Promise<PendingSocialAuth | null> => {
      if (!isSocialProvider(provider)) return null;

      switch (provider) {
        case 'kakao': {
          const code = query.get('code');
          if (code == null) return null;
          const { access_token } = await postKakaoAccessToken({
            authorization_code: code,
            redirect_uri: getAuthCallbackUri('kakao'),
          });
          return { provider, accessToken: access_token };
        }
        case 'naver':
        case 'facebook': {
          const accessToken = hash.get('access_token');
          if (accessToken == null) return null;
          return { provider, accessToken };
        }
        case 'apple': {
          const idToken = hash.get('id_token');
          if (idToken == null) return null;
          return { provider, accessToken: idToken };
        }
      }
    };

    (async () => {
      try {
        const pending = await obtainToken();
        if (pending != null) {
          // Login 마운트까지 살아남도록 SPA 이동(moveToLogin) 직전에 담는다.
          setPendingSocialAuth(pending);
        }
      } catch (error) {
        bridge.toast(
          error instanceof ApiError && error.message
            ? error.message
            : '로그인에 실패했어요',
        );
      } finally {
        navigator.moveToLogin(state);
      }
    })();
  }, [navigator, postKakaoAccessToken, setPendingSocialAuth]);
};
